// **A bee type's sprite, colour and short label** — lifted out of `ui/screens/Colony.tsx` so the
// sandbox screen can draw the same chips without importing a 2000-line live-mode screen (design
// **T1**: the sandbox screen is a host, and a host that reaches into another screen's module scope
// is not one).
//
// This is a verbatim move of Colony's module-private `PRESET_VISUALS` / `spriteForType` /
// `colorForType` / `extensionPaletteIndex`, plus the uppercase chip abbreviations the sandbox's
// bee-type bar needs (visual spec §4.1) and live mode's dock does not. Nothing about the mapping
// changed: the five shipped presets keep their token colours, and a player-defined type keeps the
// deterministic extension-palette ramp indexed by its position in the colony's type list (design
// **D10**, UI.md §2) — deterministic because a type's colour appearing to change between renders
// would make the chips unreadable as identity.
//
// **The follow-up this file's header used to defer is now done** (`pvp-ui-fixes` W1): `Colony.tsx`
// imports from here and keeps no second copy. It was collapsed because the duplication stopped
// being a tolerable risk and became the bug's shape — the same wrong URL had to be fixed twice,
// and a guarantee that "every icon the UI references is loadable" cannot be stated over a table
// that exists in two places.
//
// **Sprite URLs are root-absolute** (`/bee_harvester.png`, not `bee_harvester.png`). A bare name
// resolves against the current URL's directory, which is the same thing on `/colony` and a broken
// image on the two-segment `/demo/red` and `/review/:round`. The flat icons ship at
// `client/public/`'s root rather than under `/sprites/`, which mirrors the prototype kit's subtree
// and shares filenames with them — see `components/hud/hud-icons.ts`.

/** The five shipped presets (DESIGN §3), by lower-cased document name. */
const PRESET_VISUALS: Record<string, { sprite: string; color: string; abbrev: string }> = {
  harvester: { sprite: '/bee_harvester.png', color: 'var(--bee-harvester)', abbrev: 'HARV' },
  nurse: { sprite: '/bee_nurse.png', color: 'var(--bee-nurse)', abbrev: 'NURSE' },
  builder: { sprite: '/bee_builder.png', color: 'var(--bee-builder)', abbrev: 'BUILD' },
  scout: { sprite: '/bee_scout.png', color: 'var(--bee-scout)', abbrev: 'SCOUT' },
  warrior: { sprite: '/bee_warrior.png', color: 'var(--bee-warrior)', abbrev: 'WAR' },
};

/**
 * **The canonical preset roster order** — `0=harvester, 1=nurse, 2=builder, 3=scout, 4=warrior`,
 * the fixture registry's own numbering (`waggle-fixtures/src/factory.rs`) and therefore the order
 * a fresh colony's dock shows its types in.
 *
 * Exists for surfaces whose type list arrives **name-keyed** — the stats sample's `composition`
 * is a serde `BTreeMap`, so its keys come over the wire alphabetised (`builder` first), which is
 * an artefact of serialisation, not an order anyone chose. Sorting those names onto this order
 * makes an opponent's chips read in the same sequence as the player's own dock.
 */
export const PRESET_ROSTER_ORDER: readonly string[] = [
  'harvester',
  'nurse',
  'builder',
  'scout',
  'warrior',
];

/** Names in roster order: the presets above first, then anything else in the order given —
 * a player-defined type has no canonical slot, and its first-seen position is at least stable. */
export function orderRosterNames(names: readonly string[]): string[] {
  const preset = PRESET_ROSTER_ORDER.filter((name) => names.includes(name));
  const extra = names.filter((name) => !PRESET_ROSTER_ORDER.includes(name));
  return [...preset, ...extra];
}

/** The deterministic extension-palette index for a type at colony-type-order `index`:
 * `ramp[(index - 5) mod 7]`, wrapped so it is well-defined for any non-negative index. */
export function extensionPaletteIndex(index: number): number {
  return (((index - 5) % 7) + 7) % 7;
}

/** A type's sprite file name. Unknown names fall back to the harvester sheet rather than to a
 * broken image: a player-defined type is still a bee. */
export function spriteForType(name: string): string {
  return PRESET_VISUALS[name.toLowerCase()]?.sprite ?? '/bee_harvester.png';
}

/** A type's colour token: its preset colour by name, else the extension ramp at its position. */
export function colorForType(name: string, index: number): string {
  return (
    PRESET_VISUALS[name.toLowerCase()]?.color ?? `var(--bee-ext-${extensionPaletteIndex(index)})`
  );
}

/**
 * The uppercase label under a chip's count (visual spec §4.1). A player-defined type has no
 * curated abbreviation, so the first four characters of its own name are used — its own word,
 * truncated, rather than an invented one the player would have to learn.
 */
export function abbrevForType(name: string): string {
  return PRESET_VISUALS[name.toLowerCase()]?.abbrev ?? name.slice(0, 4).toUpperCase();
}
